import _ from 'lodash'
import QuoteHelper from "./QuoteHelper";

// Credit tiers, best (T1) → worst (T6). Each tier maps to the minimum credit
// score a customer needs to qualify for it. Lives on the frontend per the
// agreed data contract — quotes only carry their `tier` string.
export const TIER_ORDER = ['T1', 'T2', 'T3', 'T4', 'T5', 'T6']
export const TIER_SCORE = { T1: 750, T2: 720, T3: 680, T4: 640, T5: 600, T6: 0 }

// Terms pre-selected by default in the filter bar, by deal type.
export const LEASE_TERMS = ['24', '27', '30', '33', '36', '39', '42', '45', '48']
export const RETAIL_TERMS = ['36', '42', '45', '48', '60', '72', '84']

// Default term selection for a deal (retail/purchase vs lease).
export const defaultTermsForDeal = (state) =>
    state?.is_purchase ? RETAIL_TERMS : LEASE_TERMS

// Synthetic tier key used when quotes have no `tier` (degrade mode) so the
// matrix renderer can use one uniform [term][tier][lender] access path.
export const NO_TIER = '_'

// Normalize a quote's tier into canonical "T<n>" form. Accepts "T3"/"t3" or a
// positive number / numeric string (any tier, not just 1–6). Null if unknown.
export const normalizeTier = (value) => {
    if (value === null || value === undefined || value === '') return null
    const m = String(value).trim().toUpperCase().match(/^T?(\d+)$/)
    if (!m) return null
    const n = Number(m[1])
    return n >= 1 ? `T${n}` : null
}

// Numeric part of a normalized tier ("T3" → 3), for ordering.
export const tierNumber = (tier) => Number(String(tier).slice(1))

// Minimum credit score for a tier. Tiers beyond the named set (e.g. T7)
// default to 0 — sub-prime, always qualifies regardless of score.
export const tierMinScore = (tier) => TIER_SCORE[tier] ?? 0

// A quote qualifies for `score` when it falls within the quote's
// [min_credit_score, max_credit_score] band (a missing bound is unbounded).
// Used to dim (not drop) credit-ineligible quotes in the grid.
export const quotePassesCreditScore = (quote, score) => {
    const min = Number(quote.min_credit_score)
    const max = Number(quote.max_credit_score)
    if (!isNaN(min) && score < min) return false
    if (!isNaN(max) && score > max) return false
    return true
}

// Cash (one-pay purchase) deals scan with a single forced term ('36') and show a
// price chip per lender. When starting a Cash deal we skip the grid and auto-pick
// the quote the user would otherwise click: the lowest-payment quote eligible for
// the given credit score (mirrors the grid's "lowest overall" highlight). Ignores
// the term filter on purpose so the Cash quote is picked even when a saved default
// term set would hide it. Returns null when nothing is eligible — the caller then
// falls back to showing the grid.
export const pickCashQuote = (quotes, creditScore) => {
    const toNumber = (v) => parseFloat(String(v ?? '').replace(/[^0-9.-]/g, ''))
    const list = (quotes || []).filter(Boolean)
    if (list.length === 0) return null

    const scoreStr = String(creditScore ?? '').trim()
    const eligible = scoreStr === ''
        ? list
        : list.filter((quote) => quotePassesCreditScore(quote, Number(scoreStr)))
    if (eligible.length === 0) return null

    return eligible.reduce((best, quote) =>
        toNumber(quote.monthly_payment) < toNumber(best.monthly_payment) ? quote : best
    )
}

// True when a quote is captive (manufacturer) financing — tolerant of the flag
// arriving as a boolean, 1/0, or the strings "true"/"false".
export const isCaptive = (quote) => {
    const v = quote?.is_captive_lender
    return v === true || v === 1 || (typeof v === 'string' && v.toLowerCase() === 'true')
}

// A quote's grid column: each captive program has its own label (lender_label);
// other lenders, and older responses without one, use the lender name. Saving a
// quote still sends the raw lender_name and program_code.
export const lenderColumn = (quote) => quote?.lender_label || quote?.lender_name

// The program part of a captive offer's label, from its "(" on, e.g.
// "(Standard Lease Program) Flat Fee Program #2"; the whole label when it has
// none. Null when the quote has no label of its own.
export const programName = (quote) => {
    const label = quote?.lender_label
    if (!label || label === quote.lender_name) return null
    const start = label.indexOf('(')
    return start > 0 ? label.slice(start) : label
}

// Rates entered for one program, which never carry to another: "Desk as" and a
// grid switch to a different program clear them.
export const CLEARED_RATES = { rate_markup: '', interest_rate_override: '', money_factor_override: '' }

// A grid switch to a different program also clears the residual override:
// residual tables differ by program.
export const PROGRAM_SWITCH_CLEARED = { ...CLEARED_RATES, residual_override: '' }

// True when the deal has something entered for its current program: a rate or
// money factor, a non-zero markup (a zero markup is no markup; a 0% APR is a
// real override) or, on a lease, a residual override.
export const hasProgramOverride = (deal) => {
    const entered = (field) => String(deal?.[field] ?? '').trim()
    return entered('interest_rate_override') !== '' || entered('money_factor_override') !== '' ||
        (entered('rate_markup') !== '' && Number(entered('rate_markup')) !== 0) ||
        (!deal?.is_purchase && entered('residual_override') !== '')
}

// A label family: the label without its duplicate "#n" suffix.
const labelFamily = (label) => label.replace(/ #\d+$/, '')
const labelNumber = (label) => Number(label.match(/ #(\d+)$/)?.[1] ?? 1)

// Keep each label family's columns together, first-seen family first, numbered
// duplicates in order. Stable, so other columns keep their order.
export const groupLabelFamilies = (labels) => {
    const families = _.uniq(labels.map(labelFamily))
    return _.sortBy(labels, [(label) => families.indexOf(labelFamily(label)), labelNumber])
}

// Best By Lender: the grid as it was before captive offers, one quote per lender
// in each term and tier. A captive cell's best-ranked offer comes last (see
// lenders()), so keep each cell's last quote, in the cell's first position.
export const bestRankedPerLender = (quotes) =>
    [...new Map((quotes || []).map((quote) => [`${quote.term}|${quote.tier}|${quote.lender_name}`, quote])).values()]

class DeskingHelper {
    constructor(quotes, dealState) {
        this.quotes = quotes || []
        this.dealState = dealState
    }

    quotesByTermAndLender = () => {
        const map = {}
        this.quotes.map((quote) => {
            const term = quote.term
            const lender = quote.lender_name

            if (!(term in map)) {
                map[term] = {[lender]: quote}
            } else {
                map[term][lender] = quote
            }
        })

        return map
    }

    quotesByLenderAndTerm = () => {
        const map = {}
        this.quotes.map((quote) => {
            const helper = new QuoteHelper(null, quote)
            const lender = helper.lender()
            const term = helper.term()

            if (!(lender in map)) {
                map[lender] = {[term]: quote}
            } else {
                map[lender][term] = quote
            }
        })
        return map
    }

    // Nested matrix: { [term]: { [tier]: { [lender]: quote } } }.
    // In degrade mode (no tiers) every quote is bucketed under NO_TIER.
    quotesByTermTierLender = () => {
        const map = {}
        this.quotes.forEach((quote) => {
            const term = quote.term
            const tier = normalizeTier(quote.tier) || NO_TIER
            const lender = lenderColumn(quote)

            if (!map[term]) map[term] = {}
            if (!map[term][tier]) map[term][tier] = {}
            map[term][tier][lender] = quote
        })
        return map
    }

    // Column order. A captive (term, tier, lender) cell arrives best offer last,
    // for older clients that keep the last quote per lender, so read each cell
    // from its end: columns lead with the best program. Other lenders have one
    // quote per cell and keep their order.
    lenders = () => {
        const cells = new Map()
        this.quotes.forEach((quote) => {
            const key = `${quote.term}|${quote.tier}|${quote.lender_name}`
            cells.set(key, [...(cells.get(key) || []), quote])
        })
        return _.uniq([...cells.values()].flatMap((cell) => [...cell].reverse().map(lenderColumn)))
    }

    // True once the backend tags quotes with a recognizable credit tier.
    hasTiers = () => this.quotes.some((quote) => normalizeTier(quote.tier) !== null)

    // Tiers actually present in the data, ordered by number — supports fewer
    // than 6 and more than 6 (e.g. T7). Empty array signals degrade mode.
    tiers = () => {
        const present = [...new Set(this.quotes.map((q) => normalizeTier(q.tier)).filter(Boolean))]
        return present.sort((a, b) => tierNumber(a) - tierNumber(b))
    }

    // Columns whose quotes are captive (manufacturer) financing. Tolerant of the
    // flag arriving as a real boolean, 1/0, or the strings "true"/"false" — a
    // strict `=== true` silently treated a truthy "true"/1 as non-captive.
    captiveLenders = () => new Set(
        this.quotes
            .filter((quote) => isCaptive(quote))
            .map(lenderColumn)
    )

    terms = () => _.uniq(this.quotes.map((quote) => quote.term))
        .sort((a, b) => Number(a) - Number(b))

    termsForLender = (lender) => _.uniq(this.quotes.flatMap((quote) => {
        const helper = new QuoteHelper(null, quote)
        const quoteLender = helper.lender()
        if (lender === quoteLender) {
            return [Number(helper.term())]
        } else {
            return []
        }
    }))

    quotesForTerm = (term) => this.quotes.filter((quote) => {
        return String(term) === String(quote.term)
    })

    quotesForTermsSortedByBest = (term, isPurchase) => {
        const quotesForTerm = this.quotesForTerm(term)

        const sorted = quotesForTerm.sort((a, b) => {
            return Number(a.monthly_payment) - Number(b.monthly_payment)
        })

        return sorted
    }

}

export default DeskingHelper
